import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Loader2, ShieldCheck, Search, Plus, X, Package, Users, AlertTriangle } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

// v2.5.875 — Platform Admin UI for granting/revoking paid add-ons to tenants.
// Wraps POST /api/modules/admin/addon-grant. Only visible to super_admin.
// Used to comp Vending Fleet, extend trials, or bypass paywall for VIPs.
export default function AdminAddonGrantPage() {
  const { user } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [modules, setModules] = useState([]);
  const [tenantSearch, setTenantSearch] = useState('');
  const [selectedTenant, setSelectedTenant] = useState(null);
  const [selectedModule, setSelectedModule] = useState('');
  const [tenantAddons, setTenantAddons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const isSuperAdmin = (user?.role || '').toLowerCase() === 'super_admin';

  const loadInitial = useCallback(async () => {
    if (!isSuperAdmin) { setLoading(false); return; }
    setLoading(true);
    try {
      const [t, m] = await Promise.all([
        axios.get(`${API}/api/admin/clients`).catch(() => ({ data: { clients: [] } })),
        axios.get(`${API}/api/modules/registry`),
      ]);
      setTenants(t.data.clients || t.data.items || t.data.tenants || []);
      const addonMods = (m.data.modules || []).filter(x => x.is_addon);
      setModules(addonMods);
      if (addonMods.length && !selectedModule) setSelectedModule(addonMods[0].module_id);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load admin data');
    } finally { setLoading(false); }
  }, [isSuperAdmin, selectedModule]);

  const loadTenantAddons = useCallback(async (tenantId) => {
    if (!tenantId) { setTenantAddons([]); return; }
    try {
      const { data } = await axios.get(`${API}/api/modules/admin/tenant-addons`, { params: { tenant_id: tenantId } });
      setTenantAddons(data.addons || []);
    } catch (e) {
      // Endpoint might not exist yet — silent
      setTenantAddons([]);
    }
  }, []);

  useEffect(() => { loadInitial(); }, [loadInitial]);
  useEffect(() => { if (selectedTenant) loadTenantAddons(selectedTenant.tenant_id || selectedTenant.id); }, [selectedTenant, loadTenantAddons]);

  const grant = async (grantFlag) => {
    if (!selectedTenant || !selectedModule) return;
    setSaving(true);
    try {
      const tid = selectedTenant.tenant_id || selectedTenant.id;
      const { data } = await axios.post(`${API}/api/modules/admin/addon-grant`, {
        tenant_id: tid,
        module_id: selectedModule,
        grant: grantFlag,
      });
      toast.success(`${data.action === 'granted' ? 'Granted' : 'Revoked'} ${selectedModule} → ${selectedTenant.email || tid}`);
      loadTenantAddons(tid);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Grant/revoke failed');
    } finally { setSaving(false); }
  };

  const filteredTenants = tenants.filter(t => {
    if (!tenantSearch) return true;
    const q = tenantSearch.toLowerCase();
    return (t.email || '').toLowerCase().includes(q)
      || (t.full_name || '').toLowerCase().includes(q)
      || (t.company_name || '').toLowerCase().includes(q)
      || (t.tenant_id || t.id || '').toLowerCase().includes(q);
  });

  if (!isSuperAdmin) {
    return (
      <div className="max-w-3xl mx-auto p-6" data-testid="admin-addon-grant-blocked">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-red-900">Platform Admin only</div>
              <div className="text-sm text-red-800 mt-1">
                Add-on granting is restricted to Platform Admin accounts. If you need a module granted to your tenant, contact your Platform Administrator.
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-3 sm:p-6 space-y-4 sm:space-y-6" data-testid="admin-addon-grant-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ShieldCheck className="h-6 w-6 text-emerald-600" />
            <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold" data-testid="admin-addon-grant-title">
              Add-on Grants
            </h1>
            <Badge className="bg-emerald-100 text-emerald-800 border-0 text-[10px]">Platform Admin</Badge>
          </div>
          <p className="text-xs sm:text-sm text-gray-medium">Grant or revoke paid add-on modules (e.g. Vending Fleet) for any tenant. Bypasses the payment gate.</p>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-medium py-4 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Loading tenants + modules…</div>}

      {!loading && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Left: Tenant picker */}
          <Card className="lg:col-span-2">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Users className="h-4 w-4" /> Tenants ({tenants.length})</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-3 top-2.5 text-gray-medium" />
                <Input
                  value={tenantSearch}
                  onChange={(e) => setTenantSearch(e.target.value)}
                  placeholder="Search by email, name, company, or tenant ID…"
                  className="pl-9 h-9 text-sm"
                  data-testid="tenant-search"
                />
              </div>
              <div className="border rounded-lg divide-y max-h-[400px] overflow-y-auto">
                {filteredTenants.slice(0, 100).map(t => {
                  const tid = t.tenant_id || t.id;
                  const isSelected = selectedTenant && (selectedTenant.tenant_id === tid || selectedTenant.id === tid);
                  return (
                    <button
                      key={tid}
                      onClick={() => setSelectedTenant(t)}
                      className={`w-full text-left p-3 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-emerald-50' : ''}`}
                      data-testid={`tenant-row-${tid}`}
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm truncate">{t.full_name || t.email || tid}</div>
                          <div className="text-[11px] text-gray-medium truncate">{t.email} · {t.company_name || t.plan || 'unknown plan'}</div>
                          <div className="text-[10px] text-gray-400 font-mono truncate">{tid}</div>
                        </div>
                        {isSelected && <Badge className="bg-emerald-600 text-white border-0 text-[10px] flex-shrink-0">Selected</Badge>}
                      </div>
                    </button>
                  );
                })}
                {filteredTenants.length === 0 && <div className="p-4 text-center text-sm text-gray-medium">No tenants match your search.</div>}
              </div>
              {filteredTenants.length > 100 && <div className="text-[11px] text-gray-medium">Showing first 100. Refine your search to narrow.</div>}
            </CardContent>
          </Card>

          {/* Right: Grant/Revoke */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Package className="h-4 w-4" /> Grant / Revoke</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              {!selectedTenant ? (
                <div className="text-sm text-gray-medium p-3 border border-dashed rounded">Select a tenant on the left to grant or revoke add-ons.</div>
              ) : (
                <>
                  <div className="p-3 border rounded-lg bg-gray-50">
                    <div className="text-[11px] uppercase text-gray-medium mb-0.5">Target tenant</div>
                    <div className="font-medium text-sm truncate">{selectedTenant.full_name || selectedTenant.email}</div>
                    <div className="text-[11px] text-gray-medium truncate">{selectedTenant.email}</div>
                    <div className="text-[10px] font-mono text-gray-400 truncate mt-0.5">{selectedTenant.tenant_id || selectedTenant.id}</div>
                  </div>

                  <div>
                    <div className="text-xs font-medium mb-1.5">Add-on module</div>
                    <Select value={selectedModule} onValueChange={setSelectedModule}>
                      <SelectTrigger className="h-9 text-sm" data-testid="module-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {modules.map(m => (
                          <SelectItem key={m.module_id} value={m.module_id}>
                            {m.name} — ${m.addon_price}/mo · ${m.addon_one_time_price} one-time
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex gap-2">
                    <Button
                      onClick={() => grant(true)} disabled={saving || !selectedModule}
                      className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-xs sm:text-sm"
                      data-testid="grant-btn"
                    >
                      {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Plus className="h-3.5 w-3.5 mr-1" />}
                      Grant
                    </Button>
                    <Button
                      onClick={() => grant(false)} disabled={saving || !selectedModule}
                      variant="outline"
                      className="flex-1 border-red-300 text-red-700 hover:bg-red-50 h-9 text-xs sm:text-sm"
                      data-testid="revoke-btn"
                    >
                      <X className="h-3.5 w-3.5 mr-1" /> Revoke
                    </Button>
                  </div>

                  {tenantAddons.length > 0 && (
                    <div>
                      <div className="text-xs font-medium mb-1.5">Current add-ons for this tenant</div>
                      <div className="flex flex-wrap gap-1.5">
                        {tenantAddons.map(a => <Badge key={a} className="bg-sky-100 text-sky-800 border-0 text-[10px]">{a}</Badge>)}
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
